/**
 * Affiliate self dashboard — /affiliate
 *
 * Only rendered inside the AppShell for authenticated users. If the
 * user is not an active affiliate we redirect to /affiliate-program.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

function dollars(cents) {
  if (typeof cents !== "number") return "$0.00";
  const sign = cents < 0 ? "-" : "";
  const v = Math.abs(cents);
  return `${sign}$${Math.floor(v / 100)}.${String(v % 100).padStart(2, "0")}`;
}

function shortDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return "—";
  }
}

export default function AffiliateDashboardPage() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [refs, setRefs] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const meResp = await api.get("/affiliate/me");
        if (cancelled) return;
        if (!meResp.data?.is_affiliate || meResp.data.status !== "active") {
          navigate("/affiliate-program");
          return;
        }
        setMe(meResp.data);
        const [refsResp, payResp] = await Promise.all([
          api.get("/affiliate/referrals"),
          api.get("/affiliate/payouts"),
        ]);
        if (!cancelled) {
          setRefs(refsResp.data?.referrals || []);
          setPayouts(payResp.data?.payouts || []);
        }
      } catch {
        if (!cancelled) navigate("/affiliate-program");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const referralLink = useMemo(() => {
    if (!me?.affiliate_code) return "";
    const base = window.location.origin;
    return `${base}/?ref=${me.affiliate_code}`;
  }, [me]);

  const pricingLink = useMemo(() => {
    if (!me?.affiliate_code) return "";
    const base = window.location.origin;
    return `${base}/#pricing?ref=${me.affiliate_code}`;
  }, [me]);

  const copy = async (label, text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Copy failed");
    }
  };

  if (loading) {
    return <p className="p-8 text-sm text-[color:var(--text-2)]" data-testid="affiliate-dashboard-loading">Loading…</p>;
  }
  if (!me) return null;

  const stats = me.stats || {};
  const balances = me.balances || {};
  const conv = stats.clicks ? ((stats.paying_customers / stats.clicks) * 100).toFixed(1) : "0.0";

  return (
    <div className="p-6 sm:p-8 space-y-8" data-testid="affiliate-dashboard">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl">Affiliate Program</h1>
        <p className="text-sm text-[color:var(--text-2)] mt-1">
          50% commission · first 12 months · 60-day attribution · monthly manual payouts
        </p>
      </div>

      <div className="rounded-lg border border-[color:var(--border)] bg-[color:var(--bg-1)] p-6">
        <p className="text-xs uppercase tracking-widest text-[color:var(--text-2)] mb-2">
          Your unique referral link
        </p>
        <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
          <code
            className="text-sm break-all bg-[color:var(--bg-2)] px-3 py-2 rounded"
            data-testid="affiliate-referral-link"
          >
            {referralLink}
          </code>
          <Button
            onClick={() => copy("Referral link", referralLink)}
            data-testid="affiliate-copy-referral-btn"
          >
            Copy Link
          </Button>
        </div>
        <div className="mt-3 flex flex-col sm:flex-row gap-2 items-start sm:items-center text-xs text-[color:var(--text-2)]">
          <span>Also useful:</span>
          <button
            className="underline"
            onClick={() => copy("Pricing link", pricingLink)}
            data-testid="affiliate-copy-pricing-btn"
          >
            Copy pricing-page link
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Stat label="Clicks" value={stats.clicks || 0} tid="stat-clicks" />
        <Stat label="Signups" value={stats.signups || 0} tid="stat-signups" />
        <Stat label="Paid Customers" value={stats.paying_customers || 0} tid="stat-paid-customers" />
        <Stat label="Conversion Rate" value={`${conv}%`} tid="stat-conversion" />
        <Stat label="Pending Commission" value={dollars(balances.pending_cents)} tid="stat-pending" />
        <Stat label="Available Commission" value={dollars(balances.available_cents)} tid="stat-available" />
        <Stat label="Paid Commission" value={dollars(balances.paid_cents)} tid="stat-paid" />
        <Stat
          label="Net Lifetime Earnings"
          value={dollars(balances.lifetime_earned_cents)}
          tid="stat-lifetime"
          hint="Commission earned after refunds and reversals."
        />
      </div>

      <section>
        <h2 className="font-display text-xl mb-3">Your referrals</h2>
        <div className="overflow-x-auto rounded-lg border border-[color:var(--border)] bg-[color:var(--bg-1)]">
          <table className="min-w-full text-sm" data-testid="affiliate-referrals-table">
            <thead className="text-xs uppercase tracking-widest text-[color:var(--text-2)]">
              <tr>
                <th className="text-left px-4 py-3">Customer</th>
                <th className="text-left px-4 py-3">Plan</th>
                <th className="text-left px-4 py-3">Signed up</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Pending</th>
                <th className="text-right px-4 py-3">Available</th>
                <th className="text-right px-4 py-3">Paid</th>
              </tr>
            </thead>
            <tbody>
              {refs.length === 0 ? (
                <tr>
                  <td className="px-4 py-6 text-[color:var(--text-2)]" colSpan={7}>
                    No referrals yet. Share your link above.
                  </td>
                </tr>
              ) : (
                refs.map((r) => (
                  <tr key={r.referral_id} className="border-t border-[color:var(--border)]" data-testid="affiliate-referral-row">
                    <td className="px-4 py-3">{r.referred_user_masked || "—"}</td>
                    <td className="px-4 py-3">{r.plan || "—"}</td>
                    <td className="px-4 py-3">{shortDate(r.signup_at)}</td>
                    <td className="px-4 py-3 capitalize">{r.status}</td>
                    <td className="px-4 py-3 text-right">{dollars(r.commission_pending_cents)}</td>
                    <td className="px-4 py-3 text-right">{dollars(r.commission_approved_cents)}</td>
                    <td className="px-4 py-3 text-right">{dollars(r.commission_paid_cents)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h2 className="font-display text-xl mb-3">Payout history</h2>
        <div className="overflow-x-auto rounded-lg border border-[color:var(--border)] bg-[color:var(--bg-1)]">
          <table className="min-w-full text-sm" data-testid="affiliate-payouts-table">
            <thead className="text-xs uppercase tracking-widest text-[color:var(--text-2)]">
              <tr>
                <th className="text-left px-4 py-3">Paid on</th>
                <th className="text-left px-4 py-3">Method</th>
                <th className="text-left px-4 py-3">Reference</th>
                <th className="text-right px-4 py-3">Amount</th>
              </tr>
            </thead>
            <tbody>
              {payouts.length === 0 ? (
                <tr>
                  <td className="px-4 py-6 text-[color:var(--text-2)]" colSpan={4}>
                    No payouts yet. You'll see them here once your first payout is sent.
                  </td>
                </tr>
              ) : (
                payouts.map((p) => (
                  <tr key={p.payout_id} className="border-t border-[color:var(--border)]" data-testid="affiliate-payout-row">
                    <td className="px-4 py-3">{shortDate(p.paid_at)}</td>
                    <td className="px-4 py-3">{p.payment_method || "—"}</td>
                    <td className="px-4 py-3">{p.payment_reference || "—"}</td>
                    <td className="px-4 py-3 text-right">{dollars(p.amount)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value, tid, hint }) {
  return (
    <div
      className="rounded-lg border border-[color:var(--border)] bg-[color:var(--bg-1)] p-4"
      data-testid={tid}
      title={hint || undefined}
    >
      <p className="text-xs uppercase tracking-widest text-[color:var(--text-2)]">{label}</p>
      <p className="mt-2 font-display text-2xl">{value}</p>
      {hint ? (
        <p className="mt-2 text-xs text-[color:var(--text-2)]">{hint}</p>
      ) : null}
    </div>
  );
}
